import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Gift } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Brand";

const inputCls = "w-full bg-[#0B0E14] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FFB800] focus:ring-2 focus:ring-[#FFB800]/20 transition-[border-color,box-shadow] placeholder:text-gray-600";
const HERO = "https://mediumrare.imgix.net/30688668d7d2d48d472edd0f1e2bca0758e7ec51cbab8c04d8b7f157848640e0?w=720&h=940&fit=min&auto=format";

export default function AuthPage({ admin }) {
  const { signIn } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "", promo_code: "" });
  const [agree, setAgree] = useState({ age: false, terms: false });
  const [showPromo, setShowPromo] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "register" && !(agree.age && agree.terms)) { setErr("Please confirm your age and accept the terms."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/${mode}`, mode === "login" ? { email: f.email, password: f.password } : f);
      if (admin && data.user.role !== "admin") throw new Error("This account does not have admin access");
      signIn(data.token, data.user);
      nav(admin ? "/admin" : "/");
    } catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };

  const guest = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/guest");
      signIn(data.token, data.user);
      nav("/");
    } catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0B0E14]" />
        <div className="absolute bottom-12 left-12 max-w-sm">
          <h2 className="font-display text-5xl font-black leading-none">Play free.<br /><span className="gc-text">Win real prizes.</span></h2>
          <p className="mt-3 text-gray-300">10,000 GC + 2 SC welcome bonus on sign up.</p>
        </div>
      </div>
      <div className="flex items-center px-6 py-12 sm:px-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid={admin ? "admin-login-form" : "auth-form"}>
          <Logo />
          <h1 className="pt-6 font-display text-3xl font-black">{admin ? "Admin console" : mode === "login" ? "Welcome back" : "Create account"}</h1>
          {!admin && mode === "register" && (
            <div data-testid="signup-promo-banner" className="font-display text-2xl font-black leading-tight">Get up to 100% more <span className="gc-text">on your 1st purchase</span></div>
          )}
          {admin && <p className="flex items-center gap-2 text-sm text-gray-400"><ShieldCheck size={16} className="text-[#10B981]" />Staff access only</p>}
          {mode === "register" && <input data-testid="auth-name" placeholder="Display name" value={f.name} onChange={set("name")} className={inputCls} />}
          <input data-testid="auth-email" type="email" placeholder="Email" value={f.email} onChange={set("email")} className={inputCls} />
          <input data-testid="auth-password" type="password" placeholder="Password" value={f.password} onChange={set("password")} className={inputCls} />
          {mode === "register" && (
            <div className="space-y-3 text-sm text-gray-300">
              <label className="flex items-start gap-3"><input data-testid="signup-age-checkbox" type="checkbox" checked={agree.age} onChange={(e) => setAgree((a) => ({ ...a, age: e.target.checked }))} className="mt-0.5 w-5 h-5 accent-[#FFB800]" />I am at least 18 years old and not a resident of a restricted state.</label>
              <label className="flex items-start gap-3"><input data-testid="signup-terms-checkbox" type="checkbox" checked={agree.terms} onChange={(e) => setAgree((a) => ({ ...a, terms: e.target.checked }))} className="mt-0.5 w-5 h-5 accent-[#FFB800]" />I accept the Sweepstakesgames Terms of Use, Sweepstakes Rules and Privacy Policy.</label>
              {!showPromo ? (
                <button type="button" data-testid="signup-promo-toggle" onClick={() => setShowPromo(true)} className="flex items-center gap-2 text-[#8B9CFF] underline underline-offset-4"><Gift size={16} />I have a promo code</button>
              ) : (
                <input data-testid="signup-promo-input" placeholder="Promo code" value={f.promo_code} onChange={set("promo_code")} className={inputCls} />
              )}
            </div>
          )}
          {err && <div data-testid="auth-error" className="text-sm text-[#EF4444]">{err}</div>}
          <button data-testid="auth-submit-btn" disabled={busy} className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#FFB800] text-[#0B0E14] py-3.5 font-display text-lg font-extrabold hover:brightness-110 disabled:opacity-60 transition-[filter,opacity]">
            {busy && <Loader2 className="animate-spin" size={18} />}{mode === "login" ? "Log in" : "Sign up"}
          </button>
          {!admin && (
            <>
              <button type="button" data-testid="auth-switch-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-sm text-gray-400 hover:text-white transition-colors">
                {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
              </button>
              <div className="flex items-center gap-3 text-xs text-gray-600"><span className="flex-1 h-px bg-white/10" />or<span className="flex-1 h-px bg-white/10" /></div>
              <button type="button" data-testid="guest-login-btn" onClick={guest} disabled={busy} className="w-full rounded-xl border border-white/15 bg-white/5 py-3.5 font-display font-bold hover:bg-white/10 disabled:opacity-60 transition-colors">
                Continue as Guest — no email needed
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
